import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  FiMonitor, FiShoppingCart, FiTrendingUp, FiGrid, FiAward, FiStar
} from 'react-icons/fi';
import { TbRobot } from 'react-icons/tb';
import { useSiteContent } from '../context/SiteContentContext';

gsap.registerPlugin(ScrollTrigger);

const iconMap = {
  '💻': <FiMonitor />,
  '🛒': <FiShoppingCart />,
  '📈': <FiTrendingUp />,
  '🎨': <FiGrid />,
  '🖥️': <FiMonitor />,
  '🤖': <TbRobot />,
  '🎫': <FiAward />,
};

const servicesData = [
  {
    title: 'Web & Mobile Apps Development',
    description:
      "Let's take your brand to the next level. Our app development company builds apps that are not only great but also leave users saying 'wow.'",
    icon: '💻',
    href: '/services/web-mobile-apps',
  },
  {
    title: 'CMS / Ecommerce Development',
    description:
      'Our web development company brings your content management systems and eCommerce store with a one-of-a-kind digital experience.',
    icon: '🛒',
    href: '/services/cms-ecommerce',
  },
  {
    title: 'Digital Marketing & Strategy',
    description:
      'As a digital marketing company, we create campaigns that put your brand in the spotlight and deliver measurable results.',
    icon: '📈',
    href: '/services/digital-marketing',
  },
  {
    title: 'Graphic Design & Video Editing',
    description:
      'As your go-to graphic design agency, we ensure every pixel serves a purpose. Stunning visuals that captivate your audience.',
    icon: '🎨',
    href: '/services/graphic-design',
  },
  {
    title: 'UI/UX Design',
    description:
      'As a UI/UX design agency, we design experiences people actually enjoy using. From first click to final action — intentional and delightful.',
    icon: '🖥️',
    href: '/services/ui-ux-design',
  },
  {
    title: 'AI-Powered Technologies',
    description:
      'Say goodbye to the manual grind. We offer AI technology services that inject real smarts into your whole system and automate the havoc.',
    icon: '🤖',
    href: '/services/ai-technologies',
  },
];

// Grid layout: 3-3 Grid
const getColClass = (index) => {
  return 'lg:col-span-4';
};

const Services = () => {
  const { siteContent } = useSiteContent();
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef([]);

  const servicesData = [
    {
      title: siteContent?.['svc-web-mobile']?.title || 'Web & Mobile Apps Development',
      description: siteContent?.['svc-web-mobile']?.subtitle || "Let's take your brand to the next level. Our app development company builds apps that are not only great but also leave users saying 'wow.'",
      icon: '💻',
      href: '/services/web-mobile-apps',
    },
    {
      title: siteContent?.['svc-cms-ecom']?.title || 'CMS / Ecommerce Development',
      description: siteContent?.['svc-cms-ecom']?.subtitle || 'Our web development company brings your content management systems and eCommerce store with a one-of-a-kind digital experience.',
      icon: '🛒',
      href: '/services/cms-ecommerce',
    },
    {
      title: siteContent?.['svc-digital-mkt']?.title || 'Digital Marketing & Strategy',
      description: siteContent?.['svc-digital-mkt']?.subtitle || 'As a digital marketing company, we create campaigns that put your brand in the spotlight and deliver measurable results.',
      icon: '📈',
      href: '/services/digital-marketing',
    },
    {
      title: siteContent?.['svc-graphic-video']?.title || 'Graphic Design & Video Editing',
      description: siteContent?.['svc-graphic-video']?.subtitle || 'As your go-to graphic design agency, we ensure every pixel serves a purpose. Stunning visuals that captivate your audience.',
      icon: '🎨',
      href: '/services/graphic-design',
    },
    {
      title: siteContent?.['svc-ui-ux']?.title || 'UI/UX Design',
      description: siteContent?.['svc-ui-ux']?.subtitle || 'As a UI/UX design agency, we design experiences people actually enjoy using. From first click to final action — intentional and delightful.',
      icon: '🖥️',
      href: '/services/ui-ux-design',
    },
    {
      title: siteContent?.['svc-ai-tech']?.title || 'AI-Powered Technologies',
      description: siteContent?.['svc-ai-tech']?.subtitle || 'Say goodbye to the manual grind. We offer AI technology services that inject real smarts into your whole system and automate the havoc.',
      icon: '🤖',
      href: '/services/ai-technologies',
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header
      gsap.fromTo(
        headerRef.current.children,
        { y: 50, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.8, stagger: 0.2, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 80%' },
        }
      );
      // Cards
      gsap.fromTo(
        cardsRef.current,
        { y: 60, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.8, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 70%' },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <>
      <style>{`
        .svc-card {
          position: relative;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.05);
          border-top: 2px solid rgba(242,177,53,0.25);
          border-radius: 1.25rem;
          padding: 1.75rem;
          transition: all 0.45s cubic-bezier(0.4,0,0.2,1);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          text-decoration: none;
        }
        .svc-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at top left, rgba(242,177,53,0.12) 0%, transparent 65%);
          opacity: 0;
          transition: opacity 0.5s ease;
          pointer-events: none;
        }
        .svc-card:hover {
          transform: translateY(-8px);
          border-top-color: rgba(242,177,53,0.9);
          border-color: rgba(242,177,53,0.3);
          box-shadow: 0 20px 50px rgba(0,0,0,0.6), 0 -4px 25px rgba(242,177,53,0.15);
          background: rgba(255,255,255,0.04);
        }
        .svc-card:hover::before { opacity: 1; }

        .svc-icon {
          width: 44px; height: 44px;
          border-radius: 10px;
          background: rgba(242,177,53,0.1);
          border: 1px solid rgba(242,177,53,0.25);
          display: flex; align-items: center; justify-content: center;
          font-size: 1.25rem;
          transition: all 0.4s ease;
          flex-shrink: 0;
        }
        .svc-card:hover .svc-icon {
          background: #F2B135;
          transform: scale(1.1) rotate(5deg);
        }
        .svc-arrow {
          width: 28px; height: 28px;
          border-radius: 50%;
          border: 1px solid rgba(242,177,53,0.2);
          display: flex; align-items: center; justify-content: center;
          font-size: 0.75rem;
          color: rgba(242,177,53,0.5);
          transition: all 0.3s ease;
          flex-shrink: 0;
        }
        .svc-card:hover .svc-arrow {
          border-color: #F2B135;
          background: #F2B135;
          color: #000;
          transform: translateX(3px);
        }
      `}</style>

      <section id="services" ref={sectionRef} className="relative z-10 py-8 md:py-24">
        <div className="container-custom relative z-10">

          {/* ─── Header ─── */}
          <div ref={headerRef} className="max-w-4xl mx-auto text-center mb-8 md:mb-16">
            <div className="inline-flex items-center gap-2 bg-[#F2B135]/10 border border-[#F2B135]/20 px-4 py-1.5 rounded-full mb-6">
              <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-pulse" />
              <span className="text-xs font-black text-[#F2B135] uppercase tracking-[0.2em]">
                {siteContent?.servicesHeader?.badge || "Our Services"}
              </span>
            </div>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-black leading-[1.2] text-white mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {siteContent?.servicesHeader?.titlePrefix || "RojrzTech: Your"}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F2B135] to-[#ffaa00]">
                {siteContent?.servicesHeader?.titleHighlight || "Integrated Digital"}
              </span>{' '}
              {siteContent?.servicesHeader?.titleSuffix || "Solutions Partner"}
            </h2>
            <p className="text-white/70 text-lg md:text-xl font-medium leading-relaxed max-w-3xl mx-auto">
              {siteContent?.servicesHeader?.subtitle || "At RojrzTech, we provide a full spectrum of digital services — all under one roof."}
            </p>
          </div>

          {/* ─── 2-3-2 Grid ─── */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
            {servicesData.map((service, index) => (
              <Link
                key={index}
                to={service.href}
                ref={(el) => (cardsRef.current[index] = el)}
                className={`svc-card group col-span-1 md:col-span-1 ${getColClass(index)}`}
              >
                {/* Top row: icon + arrow */}
                <div className="flex items-start justify-between mb-5">
                  <div className="svc-icon text-[#F2B135] group-hover:text-black transition-colors">
                    {iconMap[service.icon] || <FiStar />}
                  </div>
                  <div className="svc-arrow">›</div>
                </div>

                {/* Title */}
                <h3
                  className="text-lg font-bold text-white mb-3 group-hover:text-[#F2B135] transition-colors duration-300 leading-snug"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-white/55 text-sm leading-relaxed group-hover:text-white/75 transition-colors duration-300 flex-1">
                  {service.description}
                </p>
              </Link>
            ))}
          </div>

        </div>
      </section>
    </>
  );
};

export default Services;